import type { Permissions } from '@/hooks/use-permissions';
import { isApplicationOpen } from '@/utils/application-status';
import { etDayMs, formatBusinessDate } from '@/utils/date-utils';

/**
 * May this person log a board decision on an improvement request?
 *
 * The client mirror of `canLogDecision` in api/exterior-applications.ts: a property manager, the
 * Admin role, or an `association_admin` holder — never a contractor. `isAssociationAdmin` is
 * already true for the Admin role and false for contractors, so the two booleans carry all of it.
 *
 * NOT the retired `is_admin` column (#500). The server stopped honouring it, so reading it here
 * offered the decision buttons to a flagged support user whose save 403s, and hid them from every
 * Admin created since.
 */
export const canLogApplicationDecision = (
	permissions: Pick<Permissions, 'isPropertyManager' | 'isAssociationAdmin'>,
): boolean => permissions.isPropertyManager || permissions.isAssociationAdmin;

export interface DecisionFields {
	status: number;
	statusLabel: string;
	decidedAt: number | null;
	decidedByName?: string | null;
}

/**
 * "Approved 10/3/2026 by Sue Kwadrat" — or null when the request is not decided (#611).
 *
 * The day is the decision's America/New_York business day, the same one the decision PDF's
 * file name uses, so the row and the filed packet never disagree near midnight. Gated on the
 * STATUS, not just the stamp: a request the needs-info send reverted keeps `decided_at`.
 */
export const decisionLine = (app: DecisionFields): string | null => {
	if (app.decidedAt == null || isApplicationOpen(app.status)) return null;
	return `${app.statusLabel} ${formatBusinessDate(etDayMs(app.decidedAt))} by ${app.decidedByName ?? 'a former user'}`;
};
